import type { KioskCheckinResponse } from '@emr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  acceptKioskConsent,
  completeKioskCheckin,
  getKioskCheckin,
  updateKioskCheckin,
} from '../../api/kiosk.ts';
import { ApiError, errorMessage } from '../../api/client.ts';
import { LogoLockup } from '../../components/Logo.tsx';
import { Button, ErrorText, Field } from '../../components/primitives.tsx';
import { registerKioskServiceWorker, useKioskManifest } from './kioskPwa.ts';
import { useKioskToken } from './useKioskToken.ts';

/**
 * The kiosk/tablet check-in surface (`BACKLOG.md` C7, `emr-po` A16) — mounted at `/kiosk`, outside
 * `RequireAuth` (`App.tsx`), the same "public route, different real auth" shape `/login` and `/help`
 * already establish. There is no staff session anywhere in this file; every request instead carries
 * the bearer token this page read from its own URL, via `api/kiosk.ts`.
 *
 * **Token delivery: a URL fragment, never a query string.** `useKioskToken.ts` reads
 * `location.hash`'s `token=...` — chosen over a query parameter for the identical reason
 * `sessionTokens.ts`'s own header gives for never putting a bearer credential in a URL a server ever
 * sees: a fragment is stripped by the browser before the request line is built, so it never reaches
 * this app's own server, never lands in an access log, and never appears in a `Referer` header if the
 * page ever links out. Staff hand the tablet over with the link already loaded (`emr-po` A16's own
 * "staff-initiated" framing) rather than a manual-entry flow — simpler for a five-minute window, and
 * the natural shape for a QR code or a pre-opened browser tab a staff member sets up between patients.
 *
 * **A linear, one-way flow, not a router with back/forward.** A kiosk sitting in an exam room has no
 * reason to support navigating backward through someone else's demographics after the tablet is handed
 * to the next patient — `step` below is plain component state, not a nested route.
 *
 * **PWA installability**: `useKioskManifest`/`registerKioskServiceWorker` (`kioskPwa.ts`) swap the
 * document's `<link rel="manifest">` to a kiosk-scoped manifest and register a kiosk-scoped service
 * worker while this page is mounted, restoring both on unmount. Genuine offline data entry is **not**
 * attempted — `BACKLOG.md` C7's own row leaves "does this need real offline capability" as a still-open
 * question — this only makes the page installable/lockable-into on a shared tablet (an app-shell cache,
 * not an offline queue for demographic writes, which must always reach the server as they happen).
 */

type Step = 'demographics' | 'consents' | 'complete';

export function KioskCheckinPage() {
  useKioskManifest();
  useEffect(() => {
    registerKioskServiceWorker();
  }, []);

  const { t } = useTranslation('kiosk');
  const token = useKioskToken();

  if (token === null) {
    return <KioskShell title={t('noTokenTitle')} body={t('noTokenBody')} />;
  }

  return <KioskFlow token={token} />;
}

function KioskFlow({ token }: { readonly token: string }) {
  const { t } = useTranslation('kiosk');
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('demographics');

  const checkinQuery = useQuery({
    queryKey: ['kiosk-checkin', token],
    queryFn: ({ signal }) => getKioskCheckin(token, signal),
    // A completed/expired/revoked token never becomes valid again mid-session, and retrying a 401
    // against it only burns time on a tablet someone is standing in front of.
    retry: false,
  });

  if (checkinQuery.isPending) {
    return <KioskShell title={t('loadingTitle')} body={t('loadingBody')} />;
  }

  if (checkinQuery.isError) {
    return <KioskTokenErrorScreen error={checkinQuery.error} />;
  }

  const checkin = checkinQuery.data;

  function refetchCheckin() {
    void queryClient.invalidateQueries({ queryKey: ['kiosk-checkin', token] });
  }

  if (step === 'demographics') {
    return (
      <DemographicsStep
        token={token}
        checkin={checkin}
        onSaved={() => {
          refetchCheckin();
          setStep(checkin.consents.length > 0 ? 'consents' : 'complete');
        }}
      />
    );
  }

  if (step === 'consents') {
    return (
      <ConsentsStep
        token={token}
        checkin={checkin}
        onAccepted={refetchCheckin}
        onContinue={() => {
          setStep('complete');
        }}
      />
    );
  }

  return <CompleteStep token={token} />;
}

function KioskTokenErrorScreen({ error }: { readonly error: unknown }) {
  const { t } = useTranslation('kiosk');
  const code = error instanceof ApiError ? error.code : null;
  if (code === 'kiosk_token_expired') {
    return <KioskShell title={t('expiredTitle')} body={t('expiredBody')} />;
  }
  if (code === 'kiosk_token_invalid') {
    return <KioskShell title={t('invalidTitle')} body={t('invalidBody')} />;
  }
  return <KioskShell title={t('errorTitle')} body={errorMessage(error)} />;
}

/** The shared full-screen layout every step (and every dead-end state) renders inside — a tablet
 * screen has no reason to look like the staff app's own multi-column shell. */
export function KioskShell({
  title,
  body,
  children,
}: {
  readonly title: string;
  readonly body?: string;
  readonly children?: React.ReactNode;
}) {
  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-2xl flex-1 flex-col justify-center p-4 sm:p-8"
    >
      <div className="mb-6 flex items-center justify-center">
        <LogoLockup size={40} />
      </div>
      <div className="rounded-panel border border-line bg-surface p-4 shadow-panel sm:p-8">
        <h1 className="m-0 mb-2 text-xl font-semibold text-ink sm:text-2xl">{title}</h1>
        {body !== undefined ? <p className="mt-0 mb-0 text-base text-ink-muted">{body}</p> : null}
        {children}
      </div>
    </main>
  );
}

// --- Step 1: demographics/insurance review-and-correct ------------------------------------------

function DemographicsStep({
  token,
  checkin,
  onSaved,
}: {
  readonly token: string;
  readonly checkin: KioskCheckinResponse;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation('kiosk');
  const patient = checkin.patient;

  const [firstName, setFirstName] = useState(patient.firstName);
  const [lastName, setLastName] = useState(patient.lastName);
  const [phone, setPhone] = useState(patient.phone ?? '');
  const [email, setEmail] = useState(patient.email ?? '');
  const [addressLine1, setAddressLine1] = useState(patient.addressLine1 ?? '');
  const [city, setCity] = useState(patient.city ?? '');
  const [state, setState] = useState(patient.state ?? '');
  const [postalCode, setPostalCode] = useState(patient.postalCode ?? '');
  const [primaryInsurancePayerName, setPrimaryInsurancePayerName] = useState(
    patient.primaryInsurancePayerName ?? '',
  );
  const [primaryInsuranceMemberId, setPrimaryInsuranceMemberId] = useState(
    patient.primaryInsuranceMemberId ?? '',
  );

  const save = useMutation({
    mutationFn: () =>
      updateKioskCheckin(token, {
        firstName,
        lastName,
        ...(phone === '' ? {} : { phone }),
        ...(email === '' ? {} : { email }),
        ...(addressLine1 === '' ? {} : { addressLine1 }),
        ...(city === '' ? {} : { city }),
        ...(state === '' ? {} : { state }),
        ...(postalCode === '' ? {} : { postalCode }),
        ...(primaryInsurancePayerName === '' ? {} : { primaryInsurancePayerName }),
        ...(primaryInsuranceMemberId === '' ? {} : { primaryInsuranceMemberId }),
      }),
    onSuccess: onSaved,
  });

  return (
    <KioskShell title={t('demographicsTitle')} body={t('demographicsBody')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
        className="mt-6 flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t('firstNameLabel')}
            value={firstName}
            required
            onChange={(event) => {
              setFirstName(event.target.value);
            }}
          />
          <Field
            label={t('lastNameLabel')}
            value={lastName}
            required
            onChange={(event) => {
              setLastName(event.target.value);
            }}
          />
        </div>
        <Field
          label={t('phoneLabel')}
          type="tel"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
        />
        <Field
          label={t('emailLabel')}
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <Field
          label={t('addressLabel')}
          value={addressLine1}
          onChange={(event) => {
            setAddressLine1(event.target.value);
          }}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field
            label={t('cityLabel')}
            value={city}
            onChange={(event) => {
              setCity(event.target.value);
            }}
          />
          <Field
            label={t('stateLabel')}
            value={state}
            onChange={(event) => {
              setState(event.target.value);
            }}
          />
          <Field
            label={t('postalCodeLabel')}
            value={postalCode}
            onChange={(event) => {
              setPostalCode(event.target.value);
            }}
          />
        </div>

        <h2 className="mt-2 mb-0 text-base font-semibold text-ink">{t('insuranceHeading')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t('insurancePayerLabel')}
            value={primaryInsurancePayerName}
            onChange={(event) => {
              setPrimaryInsurancePayerName(event.target.value);
            }}
          />
          <Field
            label={t('insuranceMemberIdLabel')}
            value={primaryInsuranceMemberId}
            onChange={(event) => {
              setPrimaryInsuranceMemberId(event.target.value);
            }}
          />
        </div>

        {save.isError ? <ErrorText message={errorMessage(save.error)} /> : null}

        <Button type="submit" pending={save.isPending} className="mt-2 w-full">
          {t('continueButton')}
        </Button>
      </form>
    </KioskShell>
  );
}

// --- Step 2: consent signing ---------------------------------------------------------------------

function ConsentsStep({
  token,
  checkin,
  onAccepted,
  onContinue,
}: {
  readonly token: string;
  readonly checkin: KioskCheckinResponse;
  readonly onAccepted: () => void;
  readonly onContinue: () => void;
}) {
  const { t } = useTranslation('kiosk');
  const accept = useMutation({
    mutationFn: (documentType: (typeof checkin.consents)[number]['type']) =>
      acceptKioskConsent(token, documentType),
    onSuccess: onAccepted,
  });

  const allAccepted = checkin.consents.every((consent) => consent.accepted);

  return (
    <KioskShell title={t('consentsTitle')} body={t('consentsBody')}>
      <ul className="mt-6 mb-6 flex list-none flex-col gap-3 p-0">
        {checkin.consents.map((consent) => (
          <li
            key={consent.type}
            className="flex items-center justify-between gap-3 rounded-control border border-line bg-field p-3"
          >
            <span className="text-base text-ink">{t(`documentLabel.${consent.type}`)}</span>
            {consent.accepted ? (
              <span className="text-sm font-medium text-success">{t('consentAccepted')}</span>
            ) : (
              <Button
                pending={accept.isPending}
                onClick={() => {
                  accept.mutate(consent.type);
                }}
              >
                {t('consentAcceptButton')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      {accept.isError ? <ErrorText message={errorMessage(accept.error)} /> : null}

      <Button disabled={!allAccepted} onClick={onContinue} className="w-full">
        {t('continueButton')}
      </Button>
    </KioskShell>
  );
}

// --- Step 3: completion ---------------------------------------------------------------------------

function CompleteStep({ token }: { readonly token: string }) {
  const { t } = useTranslation('kiosk');
  const complete = useMutation({ mutationFn: () => completeKioskCheckin(token) });

  if (complete.isSuccess) {
    return <KioskShell title={t('doneTitle')} body={t('doneBody')} />;
  }

  return (
    <KioskShell title={t('reviewCompleteTitle')} body={t('reviewCompleteBody')}>
      {complete.isError ? (
        <div className="mt-4">
          <ErrorText message={errorMessage(complete.error)} />
        </div>
      ) : null}
      <Button
        pending={complete.isPending}
        onClick={() => {
          complete.mutate();
        }}
        className="mt-6 w-full"
      >
        {t('finishButton')}
      </Button>
    </KioskShell>
  );
}
